/* eslint-disable react/jsx-no-bind */
import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertsContext } from '../shared/alerts/Alerts.context';
import LoggingService from '../shared/Logging.api';
import SpinningSpark from '../shared/components/SpinningSpark';
import adminFormStyles from '../shared/styles/adminForm.styles';
import { createEmptyTask } from './Task.model';
import TasksService from './Tasks.api';
import TaskForm from './forms/TaskForm';
import TaskLaunchModal, { TASK_REVIEW_MODES } from './forms/TaskLaunchModal';

const styles = {
  page: adminFormStyles.page,
  content: adminFormStyles.content,
  centered: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 320,
  },
  loadError: {
    ...adminFormStyles.section,
    marginX: 'auto',
    maxWidth: 560,
    p: 4,
    textAlign: 'center',
  },
  tasksButton: {
    borderRadius: '8px',
    textTransform: 'none',
  },
};

export default function TaskFormPage() {
  const { task_id: taskId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useContext(AlertsContext);
  const [initialTask, setInitialTask] = useState(() => createEmptyTask());
  const [isLoading, setIsLoading] = useState(Boolean(taskId));
  const [isSaving, setIsSaving] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [taskPendingLaunch, setTaskPendingLaunch] = useState(null);

  async function loadTask() {
    if (!taskId) {
      setInitialTask(createEmptyTask());
      setIsLoading(false);
      setLoadFailed(false);
      return;
    }

    setIsLoading(true);
    setLoadFailed(false);

    try {
      const task = await TasksService.getTaskById(taskId);
      setInitialTask(task);
    } catch (error) {
      LoggingService.error('Failed to load task', error);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadTask();
    // loadTask intentionally reruns only when the route id changes.
  }, [taskId]);

  async function saveDraft(task) {
    setIsSaving(true);

    try {
      if (task.task_id) {
        await TasksService.updateTask(task.task_id, task);
        const refreshedTask = await TasksService.getTaskById(task.task_id);
        setInitialTask(refreshedTask);
        showAlert('Draft saved!', 'success');
      } else {
        const createdTask = await TasksService.createTask(task);
        if (!createdTask.task_id) {
          throw new Error('The create response did not include a task_id.');
        }

        showAlert('Draft saved!', 'success');
        navigate(`/tasks/${createdTask.task_id}`, { replace: true });
      }
    } catch (error) {
      LoggingService.error('Failed to save task draft', error);
      showAlert('Unable to save this task. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  function reviewLaunch(task) {
    setTaskPendingLaunch(task);
  }

  function closeLaunchModal() {
    if (!isLaunching) {
      setTaskPendingLaunch(null);
    }
  }

  // A task already launched is being updated, not launched for the first
  // time: keep its original launch_date and skip the launch-only copy.
  function isUpdatingLiveTask(task) {
    return Boolean(task?.task_id && task?.launch_date);
  }

  async function launchTask() {
    if (!taskPendingLaunch) return;

    const isUpdate = isUpdatingLiveTask(taskPendingLaunch);

    setIsLaunching(true);
    const taskToSubmit = isUpdate
      ? taskPendingLaunch
      : { ...taskPendingLaunch, launch_date: moment() };

    try {
      if (taskToSubmit.task_id) {
        await TasksService.updateTask(taskToSubmit.task_id, taskToSubmit);
        const refreshedTask = await TasksService.getTaskById(
          taskToSubmit.task_id
        );
        setInitialTask(refreshedTask);
      } else {
        const createdTask = await TasksService.createTask(taskToSubmit);
        if (!createdTask.task_id) {
          throw new Error('The create response did not include a task_id.');
        }

        navigate(`/tasks/${createdTask.task_id}`, { replace: true });
      }

      setTaskPendingLaunch(null);
      showAlert(isUpdate ? 'Request updated!' : 'Request is live!', 'success');
    } catch (error) {
      LoggingService.error('Failed to launch task', error);
      showAlert(
        isUpdate
          ? 'Unable to update this request. Please try again.'
          : 'Unable to set this request live. Please try again.',
        'error'
      );
    } finally {
      setIsLaunching(false);
    }
  }

  async function endNow(task) {
    setIsEnding(true);

    try {
      await TasksService.endTask(task.task_id);
      const refreshedTask = await TasksService.getTaskById(task.task_id);
      setInitialTask(refreshedTask);
      showAlert('Request ended.', 'success');
    } catch (error) {
      LoggingService.error('Failed to end task', error);
      showAlert('Unable to end this request. Please try again.', 'error');
    } finally {
      setIsEnding(false);
    }
  }

  function renderContent() {
    if (isLoading) {
      return (
        <Box sx={styles.centered}>
          <SpinningSpark height="50" />
        </Box>
      );
    }

    if (loadFailed) {
      return (
        <Paper variant="outlined" sx={styles.loadError}>
          <Stack spacing={2} alignItems="center">
            <Typography component="h1" variant="h5">
              Task not found
            </Typography>
            <Button
              type="button"
              variant="contained"
              onClick={() => navigate('/tasks')}
              sx={styles.tasksButton}
            >
              Back to tasks
            </Button>
          </Stack>
        </Paper>
      );
    }

    return (
      <TaskForm
        initialTask={initialTask}
        isEnding={isEnding}
        isSaving={isSaving}
        onBack={() => navigate('/tasks')}
        onEndNow={endNow}
        onLaunch={reviewLaunch}
        onSaveDraft={saveDraft}
      />
    );
  }

  return (
    <Box sx={styles.page}>
      <Box sx={styles.content}>{renderContent()}</Box>
      {taskPendingLaunch && (
        <TaskLaunchModal
          isLaunching={isLaunching}
          isOpen
          mode={
            isUpdatingLiveTask(taskPendingLaunch)
              ? TASK_REVIEW_MODES.UPDATE
              : TASK_REVIEW_MODES.LAUNCH
          }
          onClose={closeLaunchModal}
          onConfirm={launchTask}
          task={taskPendingLaunch}
        />
      )}
    </Box>
  );
}
